import { z } from 'zod';

export const SUPPORTED_LOCALES = ['pt-PT', 'en', 'es', 'fr'] as const;
export const SIGNUP_ROLE_INTENTS = ['consumer', 'professional_owner'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export type SignupRoleIntent = (typeof SIGNUP_ROLE_INTENTS)[number];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MINIMUM_BIRTH_DATE = Date.UTC(1900, 0, 2);

function startOfUtcDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function parseIsoDate(value: string): number | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return timestamp;
}

function sixteenthBirthday(birthDate: Date): number {
  const year = birthDate.getUTCFullYear() + 16;
  const month = birthDate.getUTCMonth();
  const day = birthDate.getUTCDate();
  const timestamp = Date.UTC(year, month, day);

  return new Date(timestamp).getUTCMonth() === month
    ? timestamp
    : Date.UTC(year, month, day - 1);
}

export function validateMinimumAge(value: string, now: Date): 'invalidDate' | 'ageRestriction' | null {
  const timestamp = parseIsoDate(value);
  if (timestamp === null || timestamp < MINIMUM_BIRTH_DATE) return 'invalidDate';
  if (timestamp > startOfUtcDay(now)) return 'ageRestriction';

  return sixteenthBirthday(new Date(timestamp)) <= startOfUtcDay(now) ? null : 'ageRestriction';
}

/**
 * Sem `now`, a idade é medida no instante de CADA validação.
 *
 * 🔴 T1.19 (/code-review): o schema exportado nascia com `new Date()` do carregamento do módulo. Numa
 * aba aberta de um dia para o outro, quem fazia 16 anos à meia-noite continuava recusado até recarregar.
 */
function birthDateSchema(now?: Date) {
  return z.string().superRefine((value, context) => {
    const errorKey = validateMinimumAge(value, now ?? new Date());
    if (errorKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        params: { errorKey },
      });
    }
  });
}

export function createAuthSchemas(now?: Date) {
  const signUpSchema = z
    .object({
      email: z.string().email(),
      password: z.string().min(8),
      full_name: z.string().trim().min(1).max(120),
      birth_date: birthDateSchema(now),
      locale: z.enum(SUPPORTED_LOCALES),
      role_intent: z.enum(SIGNUP_ROLE_INTENTS),
      // Até 3: o maior conjunto de cadastro é o da Entrada B (T1.19b); o servidor valida o exato.
      accepted_document_ids: z.array(z.string().uuid()).min(1).max(3),
    })
    .strict();

  const signInSchema = z
    .object({
      email: z.string().email(),
      password: z.string().min(1),
    })
    .strict();

  const magicLinkSchema = z.object({ email: z.string().email() }).strict();

  const resetPasswordSchema = z
    .object({
      password: z.string().min(8),
      confirmation: z.string().min(1),
    })
    .strict()
    .superRefine(({ password, confirmation }, context) => {
      if (password !== confirmation) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['confirmation'],
          params: { errorKey: 'passwordMismatch' },
        });
      }
    });

  return { signUpSchema, signInSchema, magicLinkSchema, resetPasswordSchema };
}

const currentSchemas = createAuthSchemas();

export const signUpSchema = currentSchemas.signUpSchema;
export const signInSchema = currentSchemas.signInSchema;
export const magicLinkSchema = currentSchemas.magicLinkSchema;
export const resetPasswordSchema = currentSchemas.resetPasswordSchema;

export type SignUpInput = z.input<typeof signUpSchema>;
export type SignUpData = z.output<typeof signUpSchema>;
export type SignInInput = z.input<typeof signInSchema>;
export type MagicLinkInput = z.input<typeof magicLinkSchema>;
export type ResetPasswordInput = z.input<typeof resetPasswordSchema>;
